import { Stack, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ControlledTextInput from '../../../../components/custom-inputs/ControlledTextInput';
import { primaryDark, secondaryBlue } from '../../../../utils/color';
import { ReactComponent as StarInCloudIcon } from '../../../../assets/icons/project-icons/StarInCloudIcon.svg';
import { ReactComponent as ArrowLeftShortFill } from '../../../../assets/icons/layout-icons/ArrowLeftShortFill.svg';
import CustomButton from '../../../../components/button/CustomButton';
import CaseStudyReviewModal from './CaseStudyReviewModal';
import { Dispatch, SetStateAction, useState } from 'react';
import { CaseStudyContentDto } from 'tdc-web-backend/case-studies-content/schemas';
import useCreate from '../../../../utils/hooks/crud-hooks/useCreate';
import { useRefresh } from '../../../../utils/hooks/crud-hooks/useRefresh';

type CaseStudiesReviewFormProps = {
  data: CaseStudyContentDto;
  setShowReviewForm: Dispatch<SetStateAction<boolean>>;
};

const CaseStudiesReviewForm = ({ data, setShowReviewForm }: CaseStudiesReviewFormProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation();
  const refresh = useRefresh();
  const { mutate: requestReview } = useCreate({
    resource: 'case-studies/' + data.caseStudy + '/request-review',
  });
  const form = useForm({
    defaultValues: {
      companyName: '',
      firstName: '',
      email: '',
      companyWebsite: '',
      lastName: '',
      jobTitle: '',
    },
    mode: 'all',
  });

  const submitReview = (data: any) => {
    requestReview(
      { ...data },
      {
        onSuccess: () => {
          setIsModalOpen(false);
          setShowReviewForm(false);
          refresh();
        },
      },
    );
  };

  return (
    <Stack pt={4} spacing={1} pb={4}>
      <Stack pl={2} direction={'row'} spacing={1} alignItems={'center'}>
        <StarInCloudIcon fill={primaryDark[500]} />
        <Typography variant="heading5" color={'primaryDark.500'}>
          {t('caseStudies.requestReview')}
        </Typography>
      </Stack>
      <Typography variant="body3" pb={2} pl={2} color={'primaryDark.500'}>
        {t('caseStudies.fillPartnerCompanyInfo')}
      </Typography>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(submitReview)}>
          <Stack direction={'row'} width="100%">
            <Stack
              width="100%"
              px={2}
              spacing={4}
              sx={{
                borderRight: `1px solid ${secondaryBlue[100]}`,
              }}
            >
              <ControlledTextInput
                name="companyName"
                key="companyName"
                label={t('common.companyName')}
                placeholder={t('common.companyName')}
                required
              />
              <ControlledTextInput
                required
                name="firstName"
                key="firstName"
                label={t('common.firstName')}
                placeholder={t('common.firstName')}
              />
              <ControlledTextInput
                required
                name="email"
                key="email"
                label={t('common.email')}
                placeholder={t('common.email')}
                type="email"
              />
            </Stack>
            <Stack width="100%" px={2} spacing={4}>
              <ControlledTextInput
                required
                name="companyWebsite"
                key="companyWebsite"
                label={t('common.companyWebsite')}
                placeholder={t('common.companyWebsite')}
              />
              <ControlledTextInput
                required
                name="lastName"
                key="lastName"
                label={t('common.lastName')}
                placeholder={t('common.lastName')}
              />
              <ControlledTextInput
                required
                name="jobTitle"
                key="jobTitle"
                label={t('common.jobTitle')}
                placeholder={t('common.jobTitle')}
              />
            </Stack>
          </Stack>
          <CaseStudyReviewModal isOpen={isModalOpen} setIsOpen={setIsModalOpen} />
          <Stack direction="row" my={4} justifyContent="end" spacing={2}>
            <CustomButton
              variant="secondary"
              onClick={() => setShowReviewForm(false)}
              startIcon={<ArrowLeftShortFill fill={primaryDark[700]} width="12px" />}
            >
              {t('caseStudies.backToCaseStudy')}
            </CustomButton>
            <CustomButton disabled={!form.formState.isValid} onClick={() => setIsModalOpen(true)}>
              {t('common.proceed')}
            </CustomButton>
          </Stack>
        </form>
      </FormProvider>
    </Stack>
  );
};

export default CaseStudiesReviewForm;
